import { Calendar, CheckSquare, ChevronDown, PenLine, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { DocField } from '../types'

export function FieldChip({
  field,
  selected,
  sample,
  onSelect,
  showSourceBadge,
}: {
  field: DocField
  selected?: boolean
  sample?: string
  onSelect?: () => void
  showSourceBadge?: boolean
}) {
  const { t } = useTranslation()
  const display =
    field.source === 'otomatik' && field.variable
      ? `{{${field.variable}}}`
      : sample || field.placeholder || field.label

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group w-full rounded-lg border bg-card p-2.5 text-left transition-shadow',
        selected
          ? 'border-info ring-2 ring-info/30 shadow-sm'
          : 'border-border/80 hover:border-primary/40',
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{field.label}</span>
        {showSourceBadge ? (
          <Badge tone={field.source === 'otomatik' ? 'info' : 'neutral'} className="text-[10px]">
            {t(`hr.belgeler.field.source.${field.source}`)}
          </Badge>
        ) : null}
      </div>
      <div
        className={cn(
          'flex min-h-9 items-center gap-2 rounded-md border border-dashed border-border/80 bg-muted/20 px-2.5 text-sm',
          field.source === 'otomatik' && 'font-mono text-xs text-info',
        )}
      >
        <FieldTypeIcon type={field.type} />
        <span className="min-w-0 truncate">{display}</span>
        {field.type === 'tarih' ? <Calendar className="ml-auto size-3.5 shrink-0 text-muted-foreground" /> : null}
        {field.type === 'secim' ? <ChevronDown className="ml-auto size-3.5 shrink-0 text-muted-foreground" /> : null}
        {field.type === 'telefon' ? <Phone className="ml-auto size-3.5 shrink-0 text-muted-foreground" /> : null}
        {field.type === 'checkbox' ? <CheckSquare className="ml-auto size-3.5 shrink-0 text-muted-foreground" /> : null}
        {field.type === 'imza' ? <PenLine className="ml-auto size-3.5 shrink-0 text-muted-foreground" /> : null}
      </div>
    </button>
  )
}

function FieldTypeIcon({ type }: { type: DocField['type'] }) {
  if (type === 'imza') return <PenLine className="size-3.5 shrink-0 text-muted-foreground" />
  return null
}

export function SignatureBox({
  field,
  selected,
  onSelect,
}: {
  field: DocField
  selected?: boolean
  onSelect?: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex min-h-24 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-4 text-sm',
        selected ? 'border-info bg-info/5 ring-2 ring-info/30' : 'border-border text-muted-foreground',
      )}
    >
      <PenLine className="size-5" />
      <span className="font-medium text-foreground">{field.label}</span>
      <span className="text-xs">{t('hr.belgeler.field.signatureHint')}</span>
    </button>
  )
}
